
import { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Shield, Zap, TrendingUp, Clock, Target, Brain, Flame, Lock } from 'lucide-react';

const LOGO_SIZE = 120;

interface StrategyCardProps {
  logo: string;
  logoAlt: string;
  tagline: string;
  highlights: { icon: React.ReactNode; label: string; desc: string }[];
  description: string;
  visual: string;
  visualAlt: string;
  accentColor: 'blue' | 'red';
  learnMoreText: string;
  showLessText: string;
  expanded: boolean;
  onToggle: () => void;
}

const StrategyCard = ({
  logo,
  logoAlt,
  tagline,
  highlights,
  description,
  visual,
  visualAlt,
  accentColor,
  learnMoreText,
  showLessText,
  expanded,
  onToggle,
}: StrategyCardProps) => {

  const accentStyles = {
    blue: {
      glow: 'bg-blue-500/10',
      border: 'border-blue-500/20',
      shadow: 'rgba(59,130,246,0.3)',
      gradient: 'from-blue-500/10 via-purple-500/5 to-transparent',
      buttonBg: 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-300',
      iconColor: 'text-blue-400',
      taglineColor: 'text-blue-300',
    },
    red: {
      glow: 'bg-orange-500/10',
      border: 'border-orange-500/20',
      shadow: 'rgba(249,115,22,0.3)',
      gradient: 'from-orange-500/10 via-red-500/5 to-transparent',
      buttonBg: 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-300',
      iconColor: 'text-orange-400',
      taglineColor: 'text-orange-300',
    },
  };

  const s = accentStyles[accentColor];

  return (
    <div
      className="relative overflow-visible bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6"
      style={{ paddingTop: LOGO_SIZE / 2 + 24, marginTop: LOGO_SIZE / 2 }}
    >
      {/* Accent gradient at top */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${s.gradient} rounded-t-xl`} />

      {/* Logo stamp */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="relative">
          <div className={`absolute inset-0 ${s.glow} rounded-full blur-lg scale-110`} />
          <img
            src={logo}
            alt={logoAlt}
            style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
            className="relative object-contain drop-shadow-lg"
          />
        </div>
      </div>

      {/* Tagline */}
      <p className={`text-center text-sm font-semibold tracking-widest uppercase ${s.taglineColor} mb-6`}>
        {tagline}
      </p>

      {/* Highlights grid — fixed height to align cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 lg:min-h-[220px]">
        {highlights.map((h, i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/[0.05]">
            <div className={`mt-0.5 ${s.iconColor}`}>{h.icon}</div>
            <div>
              <p className="text-white text-sm font-semibold">{h.label}</p>
              <p className="text-gray-400 text-xs mt-0.5">{h.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Learn More / Collapse */}
      <button
        onClick={onToggle}
        className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${s.buttonBg}`}
      >
        {expanded ? showLessText : learnMoreText}
      </button>

      {/* Expandable detail section */}
      <div
        className="overflow-hidden transition-all duration-500 ease-in-out"
        style={{ maxHeight: expanded ? '800px' : '0px', opacity: expanded ? 1 : 0 }}
      >
        <div className="pt-6 space-y-5">
          {/* Visual image */}
          <div className="rounded-lg overflow-hidden border border-white/10">
            <img
              src={visual}
              alt={visualAlt}
              className="w-full h-64 object-cover"
            />
          </div>

          {/* Full description */}
          <p className="text-gray-300 text-sm leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

const StrategyShowcase = () => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => setExpanded(!expanded);

  const institutionalHighlights = [
    { icon: <Clock size={18} />, label: t('strategies.institutional.highlight1'), desc: t('strategies.institutional.highlight1Desc') },
    { icon: <Target size={18} />, label: t('strategies.institutional.highlight2'), desc: t('strategies.institutional.highlight2Desc') },
    { icon: <TrendingUp size={18} />, label: t('strategies.institutional.highlight3'), desc: t('strategies.institutional.highlight3Desc') },
    { icon: <Shield size={18} />, label: t('strategies.institutional.highlight4'), desc: t('strategies.institutional.highlight4Desc') },
  ];

  const spartanHighlights = [
    { icon: <Brain size={18} />, label: t('strategies.spartanAi.highlight1'), desc: t('strategies.spartanAi.highlight1Desc') },
    { icon: <Flame size={18} />, label: t('strategies.spartanAi.highlight2'), desc: t('strategies.spartanAi.highlight2Desc') },
    { icon: <Zap size={18} />, label: t('strategies.spartanAi.highlight3'), desc: t('strategies.spartanAi.highlight3Desc') },
    { icon: <Lock size={18} />, label: t('strategies.spartanAi.highlight4'), desc: t('strategies.spartanAi.highlight4Desc') },
  ];

  return (
    <section className="py-16 px-4">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-1 gap-12 lg:gap-8 lg:w-[50%]"> 
        {/* Institutional Strategy */}
        {/* <StrategyCard
          logo="/logo_transparent_institutional_small.png"
          logoAlt="Institutional Strategy"
          tagline={t('strategies.institutional.tagline')}
          highlights={institutionalHighlights}
          description={t('strategies.institutional.description')}
          visual="/background_images/institutional-visual.jpg"
          visualAlt="Institutional trading environment"
          accentColor="blue"
          learnMoreText={t('strategies.learnMore')}
          showLessText={t('strategies.showLess')}
          expanded={expanded}
          onToggle={handleToggle}
        /> */}

        {/* Spartan AI */}
        <StrategyCard
          logo="/logo_transparent_spartanai_small.png"
          logoAlt="Spartan AI"
          tagline={t('strategies.spartanAi.tagline')}
          highlights={spartanHighlights}
          description={t('strategies.spartanAi.description')}
          visual="/background_images/spartan-visual.jpg"
          visualAlt="Spartan AI trading technology"
          accentColor="red"
          learnMoreText={t('strategies.learnMore')}
          showLessText={t('strategies.showLess')}
          expanded={expanded}
          onToggle={handleToggle}
        />
      </div>
    </section>
  );
};

export default StrategyShowcase;
